/**
 * OpenAI Store - Centralized state management for OpenAI API key
 * Uses Zustand with persistence and base64 encoding for security
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { toast } from 'sonner'

interface OpenAIStore {
  // State
  apiKey: string | null
  isValidating: boolean
  quotaExhausted: boolean
  
  // Actions
  loadApiKey: () => void
  saveApiKey: (key: string) => Promise<void>
  clearApiKey: () => void
  validateApiKey: (key: string) => Promise<boolean>
  setValidating: (validating: boolean) => void
  setQuotaExhausted: (exhausted: boolean) => void
}

// Computed selector - checks if API key is properly configured
export const selectIsConfigured = (state: OpenAIStore): boolean => {
  return !!(state.apiKey && (state.apiKey.startsWith('sk-') || state.apiKey.startsWith('sk-proj-')))
}

// Simple base64 encoding/decoding for localStorage
const encodeKey = (key: string): string => btoa(key)
const decodeKey = (encoded: string): string => {
  try {
    return atob(encoded)
  } catch {
    return ''
  }
}

export const useOpenAIStore = create<OpenAIStore>()(
  persist(
    (set, get) => ({
      // Initial state
      apiKey: null,
      isValidating: false,
      quotaExhausted: false,
      
      // Load API key (called on store init via persist)
      loadApiKey: () => {
        // This is handled automatically by persist middleware
      },
      
      // Validate API key with OpenAI
      validateApiKey: async (key: string): Promise<boolean> => {
        // Basic format check - allow any string that looks like an API key
        if (!key || key.length < 20) {
          return false
        }

        try {
          // Use chat completions endpoint with minimal request for validation
          const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${key}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              model: 'gpt-3.5-turbo',
              messages: [{ role: 'user', content: 'test' }],
              max_tokens: 1
            })
          })

          // Check for successful response or specific error codes
          if (response.ok) return true
          
          // 401 = invalid key, 429 = valid key but rate limited, 403 = valid but no access
          if (response.status === 429 || response.status === 403) return true
          
          return false
        } catch (error) {
          console.error('API key validation failed:', error)
          return false
        }
      },
      
      // Save API key with validation
      saveApiKey: async (key: string): Promise<void> => {
        set({ isValidating: true })

        try {
          const isValid = await get().validateApiKey(key)
          
          if (!isValid) {
            throw new Error('Invalid OpenAI API key. Make sure you\'ve copied the complete key including all characters.')
          }

          // Update state (persist middleware will handle localStorage)
          // Clear quota exhaustion when setting new key
          set({ apiKey: key, quotaExhausted: false })
          
          toast.success('OpenAI API key saved successfully!')
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to save API key'
          toast.error(message)
          throw error
        } finally {
          set({ isValidating: false })
        }
      },
      
      // Clear API key
      clearApiKey: () => {
        try {
          set({ apiKey: null })
          toast.success('OpenAI API key removed')
        } catch (error) {
          console.error('Failed to clear API key:', error)
          toast.error('Failed to clear API key')
          throw error
        }
      },
      
      // Set validating state
      setValidating: (validating: boolean) => {
        set({ isValidating: validating })
      },
      
      // Set quota exhausted state
      setQuotaExhausted: (exhausted: boolean) => {
        set({ quotaExhausted: exhausted })
      }
    }),
    {
      name: 'loadline-openai-storage',
      // Custom storage with base64 encoding
      storage: {
        getItem: (name: string) => {
          try {
            const value = localStorage.getItem(name)
            if (!value) return null
            
            const parsed = JSON.parse(value)
            // Decode the API key if it exists
            if (parsed.state?.apiKey) {
              parsed.state.apiKey = decodeKey(parsed.state.apiKey)
            }
            return parsed
          } catch (error) {
            console.error('Failed to load OpenAI store:', error)
            return null
          }
        },
        setItem: (name: string, value: { state: OpenAIStore; version: number }) => {
          try {
            // Encode the API key before storing
            const toStore = { ...value }
            if (toStore.state?.apiKey) {
              toStore.state.apiKey = encodeKey(toStore.state.apiKey)
            }
            localStorage.setItem(name, JSON.stringify(toStore))
          } catch (error) {
            console.error('Failed to save OpenAI store:', error)
          }
        },
        removeItem: (name: string) => {
          try {
            localStorage.removeItem(name)
          } catch (error) {
            console.error('Failed to remove OpenAI store:', error)
          }
        }
      },
      // Only persist the API key, not validation state
      partialize: (state) => ({ apiKey: state.apiKey })
    }
  )
)